import { ScrollingModule } from '@angular/cdk/scrolling'
import { AsyncPipe } from '@angular/common'
import { Component, inject, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { AlertController, IonApp, IonRouterOutlet, ToastController } from '@ionic/angular/standalone'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'

import { AlertComponent } from './shared/components/alert/alert.component'
import { DebuggingModalComponent } from './shared/components/debugging-modal/debugging-modal.component'
import { LoadingComponent } from './shared/components/loading/loading.component'
import { PlaceBetModalComponent } from './shared/components/place-bet-modal/place-bet-modal.component'
import { PlaceBetModalService } from './shared/components/place-bet-modal/place-bet-modal.service'
import { ToastComponent } from './shared/components/toast/toast.component'
import { SUPPORTED_LANGUAGES } from './shared/constants/common.constant'
import { DomainService } from './shared/services/domain.service'
import { HelperService } from './shared/services/helper.service'
import { ThemeService } from './shared/services/theme.service'
import { WhiteLabelService } from './shared/services/white-label.service'

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	standalone: true,
	imports: [
		AlertComponent,
		ToastComponent,
		IonApp,
		IonRouterOutlet,
		FormsModule,
		ScrollingModule,
		LoadingComponent,
		DebuggingModalComponent,
		AsyncPipe,
		PlaceBetModalComponent,
	],
})
export class AppComponent implements OnDestroy {
	private readonly subscriptions$: Subscription = new Subscription()

	domainService = inject(DomainService)

	constructor(
		private readonly translateService: TranslateService,
		private readonly helperService: HelperService,
		private readonly themeService: ThemeService,
		protected readonly placeBetModalService: PlaceBetModalService,
		protected readonly whiteLabelService: WhiteLabelService,
		private readonly alertController: AlertController,
		private readonly toastController: ToastController,
		private readonly router: Router,
	) {
		this.translateService.addLangs(SUPPORTED_LANGUAGES.map((language) => language.symbol))
		this.translateService.setDefaultLang('en')
		const savedLang = this.helperService.getSelectedLanguage() || 'en'
		this.translateService.use(savedLang)
		this.themeService.initTheme()

		// Clear any legacy Google Translate cookies on boot
		this.helperService.clearTranslateCookie()

		if (Capacitor.isNativePlatform()) {
			// Notify Capgo updater that the app started successfully and didn't crash
			CapacitorUpdater.notifyAppReady().catch((err) => {
				console.error('Failed to notify Capgo App Ready:', err)
			})

			this.checkForUpdates()
		}
	}

	private async showDownloadingToast() {
		const toast = await this.toastController.create({
			message: this.translateService.instant('Downloading an essential app update...'),
			duration: 5000,
			position: 'bottom',
			color: 'primary',
		})
		await toast.present()
	}

	private async promptRestartForUpdate(versionId: string) {
		const alert = await this.alertController.create({
			header: this.translateService.instant('Update Ready'),
			message: this.translateService.instant('A new application update has been downloaded. The app needs to restart to apply it.'),
			backdropDismiss: false,
			buttons: [
				{
					text: this.translateService.instant('Restart Now'),
					handler: () => {
						// This forces Capgo to apply the update immediately and restart the WebView
						CapacitorUpdater.set({ id: versionId })
					},
				},
			],
		})
		await alert.present()
	}

	private async checkForUpdates() {
		console.log('***CAPACITOR UPDATE URL***', this.domainService.capacitorUpdateUrl)
		try {
			// 1. Download the update JSON file from your S3 bucket
			const response = await fetch(this.domainService.capacitorUpdateJsonUrl)
			if (!response.ok) {
				console.log('No update.json found or failed to fetch.')
				return
			}

			const updateInfo = await response.json()

			// 2. Check if we already have this exact version running
			const current = await CapacitorUpdater.current()

			// If Capgo doesn't have an OTA bundle running yet, current.bundle is null.
			// Compare the version defined in update.json against the currently running bundle.
			if (current.bundle && current.bundle.version === updateInfo.version) {
				console.log('App is already running the latest OTA version:', updateInfo.version)
				return
			}

			console.log('New update found:', updateInfo.version)

			// Show toast that background download is starting
			this.showDownloadingToast()

			// 3. Download the update in the background
			const version = await CapacitorUpdater.download({
				url: this.domainService.capacitorUpdateUrl,
				version: updateInfo.version,
			})

			if (version) {
				console.log('Update downloaded successfully', version)
				// Capgo has autoUpdate disabled to stop querying the cloud server,
				// so we must tell it to queue this bundle for the next app launch manually.
				await CapacitorUpdater.next({ id: version.id })
				console.log('Update queued for next launch!')

				// Prompt the user to restart
				this.promptRestartForUpdate(version.id)
			}
		} catch (error) {
			console.error('Error checking for updates:', error)
		}
	}

	ngOnDestroy(): void {
		this.subscriptions$.unsubscribe()
	}
}
